import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CrudComponent, CrudTableComponentInterface } from '@hutsix/ngxh6';

export interface ClosureRow {
    id: string;
    location?: { id: string; name: string } | null;
    type: string;
    startDate: string;
    endDate: string;
    reason?: string | null;
    enforcement?: string;
    active: boolean;
}

/**
 * Config -> Closures list.
 *
 * Explicit columns rather than the inferred ones: an island-wide closure has no
 * location at all, so the auto-table's guess at the shape depends on which row comes
 * first. Status is the column staff scan for - a lifted closure stays in the list so
 * there's a record of it, and without the flag the two look identical.
 *
 * Four states, not two. The tick on the form only means the closure has not been
 * lifted; whether a place is shut today is a question about the dates. Showing
 * "In force" for a closure that starts in November made the dashboard, which counts
 * only closures covering today, look broken.
 */
@Component({
    selector: 'app-closures-table',
    templateUrl: './closures-table.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ClosuresTableComponent implements CrudTableComponentInterface {
    @Input() public response: any;
    @Input() public crud!: CrudComponent;
    @Input() public complete!: boolean;

    public typeLabel(type: string): string {
        return { cultural: 'Cultural', seasonal: 'Seasonal', other: 'Other', office: 'Office closed' }[type] ?? type;
    }

    /** An office-closed day is a non-working day for the permit lockout, not a place shut to visitors. */
    public isOffice(row: ClosureRow): boolean {
        return row.type === 'office';
    }

    /**
     * Who a closure actually turns away. Everyone is warned regardless; this is the
     * short version of the "Applies to" choice on the form. Defaults to blocking the
     * public, which is how closures behaved before the setting existed.
     */
    public enforcementLabel(enforcement?: string): string {
        // "Public & businesses", not "Everyone": staff issuing by hand are never blocked,
        // and TLC read "Everyone" as blocking themselves too.
        return { block_public: 'Public blocked', block_all: 'Public & businesses', advisory: 'Warn only' }[enforcement ?? 'block_public'] ?? 'Public blocked';
    }

    /** A closure whose last day has passed is history, not a current restriction. */
    public isPast(row: ClosureRow): boolean {
        return !!row.endDate && new Date(row.endDate) < ClosuresTableComponent.today();
    }

    /** Recorded ahead of time, and not shutting anything yet. */
    public isScheduled(row: ClosureRow): boolean {
        return !!row.startDate && new Date(row.startDate) > ClosuresTableComponent.today();
    }

    /** Closed today: the only state that refuses an application dated now. */
    public isInForce(row: ClosureRow): boolean {
        return row.active && !this.isPast(row) && !this.isScheduled(row);
    }

    /** Midnight local, so a closure starting or ending today counts as covering today. */
    private static today(): Date {
        return new Date(new Date().toDateString());
    }
}
